import React, { useMemo } from 'react';
import MyNotesPageView from "../page-components/my-notes/my-notes-page.view";
import { useBaseComponent } from "../base/base-component";
import { FilterType, IState, SortType } from "../page-components/my-notes/type/my-notes-page.types";
import { Note, NoteStatus } from "../domains/models/note";
import dayjs from 'dayjs';
import { deleteNote, getNotes, markNoteAsPinned, markStatus }
    from "../page-components/my-notes/action/my-notes-page.actions";


const MyNotesPage: React.FC = () => {
    const {state, updateState} = useBaseComponent<{}, IState>({}, {
        initialState: () => ({
            data: [],
            internal: {
                modalState: {
                    open: false,
                    mode: 'add',
                    noteData: null
                },
                sortOptionSelected: 'descending'
            }
        }),
        onInit: () => onInit()
    });


    async function onInit() {
        const notes = await getNotes();
        if (notes) {
            // const formatNotes=formatNotes(notes)
            updateState('data', notes);
        }
    }

    function openAddNoteModal() {
        updateState('internal.modalState', {
            open: true,
            mode: 'add',
            noteData: null
        })
    }

    function closeAddNoteModal() {
        updateState('internal.modalState', {
            open: false,
            mode: 'add',
            noteData: null
        })
    }

    function openEditNoteModal(note: Note) {
        updateState('internal.modalState', {
            open: true,
            mode: 'edit',
            noteData: note
        })
    }

    function upsertNotes(note: Note) {
        const exists = state.data.some((e) => e.id === note.id);

        if (exists) {
            updateState(
                'data',
                state.data.map((i) => (i.id === note.id ? note : i))
            );
        } else {
            updateState('data', [note, ...state.data]);
        }
    }


    async function handleNoteDelete(id: string) {
        const hasDeleted = await deleteNote(id);
        if (hasDeleted) {
            const updatedNotes = state.data.filter((note) => note.id !== id);
            updateState('data', updatedNotes);
        }

    }

    async function handleNotesPinned(id: string) {
        const note = state.data.find((i) => i.id === id)!;
        const isPinned = !note.pinned
        const res = await markNoteAsPinned(id, isPinned);
        note.pinned = isPinned;

        updateState(
            'data',
            state.data.map((i) => (i.id === id ? note : i))
        );
    }

    async function handleNoteMarkCompleted(id: string) {
        let noteStatus: NoteStatus;
        const markNoteComplete = state.data.find((i) => i.id === id)!;
        if (markNoteComplete.status === 'ACTIVE') {
            noteStatus = 'COMPLETED'
            markNoteComplete.status = 'COMPLETED';
        } else {
            noteStatus = 'ACTIVE'
            markNoteComplete.status = 'ACTIVE';
        }
        const res = await markStatus(id, noteStatus);
        updateState(
            'data',
            state.data.map((i) => (i.id === id ? markNoteComplete : i))
        );
    }

    async function loadNotes({filter, search}: {
        filter: FilterType;
        search?: string;
    }) {

        const searchData: any = {};

        if (search) {
            searchData.title = search;
        }

        switch (filter) {
            case 'priority':
                searchData.priority = 'HIGH';
                break;

            case 'completed':
                searchData.status = 'COMPLETED';
                break;

            case 'scheduled':
                searchData.type = 'TODO';
                break;
        }

        const res = await getNotes(searchData);

        if (!res) return;

        let sortedData = [...res];


        updateState('data', sortedData);
    }

    function sortNotes(sort: SortType) {

        const priorityOrder: Record<string, number> = {
            HIGH: 1,
            MEDIUM: 2,
            LOW: 3,
            NONE: 4
        };

        return [...state.data].sort((a, b) => {

            //  pinned first
            if (a.pinned !== b.pinned) {
                return a.pinned ? -1 : 1;
            }

            // active before completed
            if (a.status !== b.status) {
                return a.status === 'COMPLETED' ? 1 : -1;
            }

            //  apply selected sorting
            switch (sort) {

                case "ascending":
                    return (
                        dayjs(a.auditInfo?.createdOn).valueOf() -
                        dayjs(b.auditInfo?.createdOn).valueOf()
                    );

                case "descending":
                    return (
                        dayjs(b.auditInfo?.createdOn).valueOf() -
                        dayjs(a.auditInfo?.createdOn).valueOf()
                    );

                case "priority":
                    return (
                        priorityOrder[a.priority ?? "NONE"] -
                        priorityOrder[b.priority ?? "NONE"]
                    );

                case "alphabetical":
                    const A = (a.title || a.content || "").toLowerCase();
                    const B = (b.title || b.content || "").toLowerCase();
                    return A.localeCompare(B);

                default:
                    return 0;
            }
        });
    }

    function handleSortChange(sort: SortType) {
        updateState('internal.sortOptionSelected', sort);
    }

    const sortedNote = useMemo(() => {
        return sortNotes(state.internal.sortOptionSelected)
    }, [state.data, state.internal.sortOptionSelected])

    const noteCount = useMemo(() => {
        let completed = 0, total = 0, overdue = 0;
        const today = dayjs();
        state.data.map((note) => {
            const dueDate = dayjs(note.dueDate);
            if (note.status === 'COMPLETED')
                completed++;
            if (dueDate.isBefore(today, 'date')) {
                overdue++;
            }
            total++;
        });

        return {
            completed: completed,
            total: total,
            overdue: overdue
        }
    }, [state.data]);

    return (
        <MyNotesPageView
            noteCount={noteCount}
            notesList={sortedNote}
            modalState={state.internal.modalState}
            upsertNotes={upsertNotes}
            openAddNoteModal={openAddNoteModal}
            closeAddNoteModal={closeAddNoteModal}
            openEditNoteModal={openEditNoteModal}
            handleNoteDelete={handleNoteDelete}
            handleNotesPinned={handleNotesPinned}
            handleNoteMarkCompleted={handleNoteMarkCompleted}
            loadNotes={loadNotes}
            sortOptionSelected={state.internal.sortOptionSelected}
            handleSortChange={handleSortChange}
        />
    )
}

export default MyNotesPage
